<template>
  <div
    class="storage-file-upload"
    :class="{
      'storage-file-upload--no-content': !$slots.content && !$slots.empty,
    }"
  >
    <FileUpload
      :mode="hasCustomTemplate ? 'advanced' : 'basic'"
      :accept="accept"
      auto
      custom-upload
      :multiple="multiple"
      :loading="isUploading"
      :max-file-size="maxFileSize"
      @uploader="uploadFiles"
    >
      <template #header="headerProps">
        <slot
          name="header"
          v-bind="{ ...headerProps, isUploading }"
        />
      </template>

      <template #content="contentProps">
        <slot
          name="content"
          v-bind="contentProps"
        />
      </template>

      <template #empty>
        <slot name="empty" />
      </template>
    </FileUpload>
  </div>
</template>

<script lang="ts" setup>
// TODO: Refactor to use VueFire methods

import {
  getStorage,
  ref as firebaseStorageRef,
  uploadBytesResumable,
  type UploadTaskSnapshot,
} from 'firebase/storage'
import type { FileUploadUploaderEvent } from 'primevue/fileupload'
import type { StorageFileUploadItem } from '../../types/components/ui/storage-file-upload'

const props = withDefaults(
  defineProps<{
    path?: string
    customPathFormatter?: (file: File) => string
    fileName?: string
    accept?: string
    maxFileSize?: number
    multiple?: boolean
  }>(),
  {
    path: 'uploads',
    accept: 'image/*',
    maxFileSize: 3145728,
    multiple: false,
  },
)
const emit = defineEmits(['uploadingStart', 'fileUploaded', 'uploaded'])

const hasCustomTemplate = !!useSlots().header
const uploadingItems = ref<StorageFileUploadItem[]>([])
const isUploading = ref(false)

const uploadFiles = async (event: FileUploadUploaderEvent) => {
  isUploading.value = true
  const storage = getStorage()
  emit('uploadingStart', {
    uploadingItems: uploadingItems.value, // Reactive values before uploading start
    files: event.files,
  })

  const uploadPromises = (event.files as File[]).map((file, fileIndex) => new Promise<StorageFileUploadItem>((resolve, reject) => {
    const storageRef = firebaseStorageRef(
      storage,
      `${props.customPathFormatter ? props.customPathFormatter(file) : `${props.path}/${props.fileName ?? file.name}`}`,
    )
    const uploadTask = uploadBytesResumable(storageRef, file)
    uploadTask.on('state_changed',
      (snapshot: UploadTaskSnapshot) => {
        uploadingItems.value[fileIndex] = {
          snapshot,
          file,
          uploadProgress: Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100),
        }
      },
      (error) => {
        // List of error codes => https://firebase.google.com/docs/storage/web/handle-errors
        const { $sentryCaptureException } = useNuxtApp()
        $sentryCaptureException(error)
        reject(error)
      },
      () => {
        uploadingItems.value[fileIndex] = { snapshot: uploadTask.snapshot, file, uploadProgress: 100 }
        resolve({ snapshot: uploadTask.snapshot, file, uploadProgress: 100 })
        emit('fileUploaded', { snapshot: uploadTask.snapshot, file, uploadProgress: 100 })
      },
    )
  }),
  )

  try {
    const items = await Promise.all(uploadPromises)
    emit('uploaded', props.multiple ? items : items[0])
  }
  catch (error) {
    const { $sentryCaptureException } = useNuxtApp()
    $sentryCaptureException(error)
  }
  finally {
    uploadingItems.value = []
    isUploading.value = false
  }
}

defineExpose({
  uploadFiles,
})
</script>

<style lang="scss">
.storage-file-upload {
  .p-fileupload-header,
  .p-fileupload-content {
    padding: 0;
  }

  .p-fileupload-advanced {
    border: 0;
    border-radius: 0;
  }

  &--no-content {
    .p-fileupload-content {
      display: none;
    }
  }
}
</style>
